import React, { useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Stack,
  Box,
  Typography as Muitypo,
  Card,
  Grid,
  ButtonGroup,
  IconButton,
  Skeleton,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  Button,
  Label,
  Link,
  Tabs,
  TextField,
  Typography,
  Chip,
  DatePicker,
  Pagination,
  Center,
  Select,
} from '../../components';

import VehicleCompare from '../VehicleCompare';
import { GetAssignmentRes } from '../../types/assignments';
import {
  approveAssignment,
  cancelAssignment,
  getAssignments,
  getUnAssignedOrders,
  requestAssignment,
  TGetAssignmentParam,
} from '../../services/assignment';
import useConfirm from '../../hooks/useConfirm';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { GLOBAL_USER } from '../../utils/constants';

interface TRequests extends GetAssignmentRes {
  decision?: 'Cancelled' | 'Requested';
}

type RequestStatus = 'pending' | 'approved' | 'rejected' | 'on hold';

interface StatusBtn {
  label: string;
  type: RequestStatus;
}

const statusButtons: StatusBtn[] = [
  { label: 'Pending', type: 'pending' },
  { label: 'Approved', type: 'approved' },
  { label: 'Rejected', type: 'rejected' },
  { label: 'On Hold', type: 'on hold' },
];

const Loader = ({ qty = 7 }) => {
  return (
    <Grid container p={1}>
      <Grid xs={5}>
        {Array.from({ length: qty }).map(() => (
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ my: 1, borderRadius: 2 }} />
        ))}
      </Grid>
      <Grid xs={7} pl={2}>
        <Skeleton variant="rectangular" width="100%" height={500} sx={{ my: 1, borderRadius: 2 }} />
      </Grid>
    </Grid>
  );
};

const Requests = () => {
  const { confirm } = useConfirm();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const [selectedStatus, setSelectedStatus] = useState<RequestStatus>('pending');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = React.useState(1);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [requests, setRequests] = useState<TRequests[]>([]);
  const [selected, setRequestSelected] = useState(0);
  const [assignableOrders, setAssignAbleOrders] = useState<any[]>([]);
  const [selectedAssignableOrders, setSelectedAssignAbleOrders] = useState<any>(null);
  const [transactionErrors, setTransactionErrors] = useState<any>();

  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const options = assignableOrders.map((o) => ({ label: o.order.orderId, value: o.order.orderId }));

  useEffect(() => {
    if (!(startDate && endDate) && requests.length !== 0) {
      return;
    }
    setLoading(true);
    const payload: TGetAssignmentParam = {
      requestor: GLOBAL_USER,
    };
    if (startDate) {
      payload.fromDate = startDate?.toISOString();
    }
    if (endDate) {
      payload.toDate = endDate?.toISOString();
    }
    getAssignments(payload)
      .then((res) => setRequests(res))
      .catch()
      .finally(() => setLoading(false));
  }, [endDate]);

  const optRequests = useMemo(() => {
    return requests.filter((r) => r.status.toLowerCase() === selectedStatus);
  }, [requests, selectedStatus]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return optRequests.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, optRequests]);

  const requestSelected = paginated[selected];
  useEffect(() => {
    if (requestSelected && selectedStatus === 'on hold') {
      setAssignAbleOrders([]);
      setSelectedOrder('');
      getUnAssignedOrders({ customerId: requestSelected.customerVehicle.customerId }).then((res) => {
        setSelectedOrder(res[0]);
        setAssignAbleOrders(res);
        setSelectedAssignAbleOrders(res[0]);
        console.log(selectedAssignableOrders?.customerVehicle, 'selectedAssignableOrders');
      });
    }
  }, [requestSelected, selectedStatus]);

  const onChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const getVariant = (p: RequestStatus) => {
    if (p === selectedStatus) {
      return 'contained';
    }
    return 'outlined';
  };

  const handleChangePage = (newPage: number) => {
    setRequestSelected(0);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (n: number) => {
    setRequestSelected(0);
    setRowsPerPage(n);
    setPage(1);
  };

  const RequstAssignmentBody = (
    <Stack py={1}>
      <Stack direction="row" spacing={4}>
        <Box>
          <Muitypo variant="body1">Order Number</Muitypo>
          <Muitypo variant="caption">{selectedOrder?.order?.orderId || 'N/A'}</Muitypo>
        </Box>
        <Box>
          <Muitypo variant="body1">Customer Name</Muitypo>
          <Muitypo variant="caption">{requestSelected?.customerName || 'N/A'}</Muitypo>
        </Box>
      </Stack>
    </Stack>
  );

  const CancelRequestBody = (
    <Stack py={1} spacing={2}>
      <Box
        p={1}
        mx={1}
        sx={{ color: '#c3493b', display: 'flex', border: '1px solid #c3493b', borderRadius: 2, alignItems: 'center' }}
      >
        <Icon icon="charm:circle-warning" color="#c3493b" />
        <Muitypo variant="caption" ml={1}>
          Vehicle will be available in the inventory once it is cancelled.
        </Muitypo>
      </Box>
      <Stack>
        <Muitypo variant="body1">VIN</Muitypo>
        <Muitypo variant="caption">{requestSelected?.inventoryVehicle.vin || 'N/A'}</Muitypo>
      </Stack>
    </Stack>
  );

  const handleRequestAssignment = async () => {
    const isConfirmed = await confirm({
      title: 'Request Assignment',
      description: `Please confirm if you would like to request assignment.`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Request Assignment',
      confirmBody: RequstAssignmentBody,
    });
    if (isConfirmed) {
      setProcessingRequest(true);
      try {
        const data = {
          orderId: selectedOrder?.order?.orderId,
          requestId: requestSelected?.requestNumber,
          requestor: GLOBAL_USER,
          reason: requestSelected.comments,
        };
        const res = await requestAssignment(data);
        // const idx = requests.findIndex((a) => a.requestNumber === requestSelected.requestNumber);
        // if (idx !== -1) {
        //   const newItem = requests[idx];
        //   newItem.decision = 'Requested';
        //   newItem.status = 'Pending';
        //   setRequests((prev) => {
        //     prev[idx] = newItem;
        //     return prev;
        //   });
        // }
        if (res.message === 'Success') {
          setProcessingRequest(false);
          showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
        } else if (res?.transactionErrors?.requestId) {
          setProcessingRequest(false);
          setTransactionErrors(res?.transactionErrors);
        } else {
          setProcessingRequest(false);
          showErrorSnackbar({ message: res.message });
        }
        // setRequests((prev) => {
        //   const rmd = prev.filter((a) => a.requestNumber !== data.requestId);
        //   return rmd;
        // });
        // showSuccessSnackbar({
        //   message: `The assignment request placed!`,
        // });
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setProcessingRequest(false);
      }
    }
  };

  const handleCancelRequest = async () => {
    const isConfirmed = await confirm({
      title: 'Cancel Request',
      description: `Are you sure you want to cancel this request?`,
      cancelButtonText: 'No, Keep this order',
      confirmButtonText: 'Yes, Cancel',
      confirmBody: CancelRequestBody,
    });
    if (isConfirmed) {
      setProcessingRequest(true);
      try {
        const requestId = requestSelected?.requestNumber || '';
        const data = {
          requestId: requestSelected?.requestNumber,
          requestor: GLOBAL_USER,
          reason: requestSelected.comments,
        };
        const cancelAssignmentResp = await cancelAssignment(data);
        setRequests((prev) => {
          const n = prev.filter((a) => a?.requestNumber !== requestId);
          return n;
        });
        setRequestSelected(0);
        if (cancelAssignmentResp.message !== 'Success') {
          showErrorSnackbar({
            message: cancelAssignmentResp.message,
          });
        } else {
          showSuccessSnackbar({
            message: `The request ${requestSelected?.requestNumber} has been cancelled successfully.`,
          });
        }
      } catch (error) {
        showErrorSnackbar({ message: 'Something went wrong. Try again.' });
      } finally {
        setProcessingRequest(false);
      }
    }
  };

  const retryAssignment = async () => {
    setProcessingRequest(true);
    try {
      const res = await approveAssignment(requestSelected?.requestNumber, GLOBAL_USER, true);
      if (res.message === 'Success') {
        setProcessingRequest(false);
        showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
      } else if (res?.transactionErrors?.requestId) {
        setProcessingRequest(false);
        setTransactionErrors(res?.transactionErrors);
      } else {
        setProcessingRequest(false);
        showErrorSnackbar({ message: res.message });
      }
    } catch (error) {
      showErrorSnackbar({ message: 'Something went wrong. Try again.' });
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box p={0.5} mt={1}>
      <Grid container>
        <Grid item xs={5}>
          <Box my={1} display="flex" justifyContent="space-between">
            <ButtonGroup size="small">
              {statusButtons.map((btn) => (
                <Button key={btn.type} variant={getVariant(btn.type)} onClick={() => setSelectedStatus(btn.type)}>
                  {btn.type}
                </Button>
              ))}
            </ButtonGroup>
            <DatePicker
              selected={startDate}
              onChange={onChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              placeholderText="Select Date"
            />
          </Box>
          <Box
            sx={{
              p: (theme) => theme.spacing(1, 0, 3, 0),
              overflowY: 'auto',
              height: 'auto',
            }}
          >
            {paginated?.length ? (
              <>
                {paginated.map((pg, idx) => (
                  <Card
                    key={pg?.requestNumber}
                    elevation={2}
                    variant="outlined"
                    sx={{
                      my: 1,
                      cursor: 'pointer',
                      padding: '15px 15px 15px 15px',
                      borderColor: (theme) =>
                        pg?.requestNumber === requestSelected?.requestNumber ? theme.palette.primary.main : null,
                      backgroundColor: () => (pg?.requestNumber === requestSelected?.requestNumber ? '#eee' : '#fff'),
                    }}
                    onClick={() => setRequestSelected(idx)}
                  >
                    <Grid container>
                      <Grid item xs={12} md={4} lg={3}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            REQUEST NUMBER
                          </Typography>
                          <Typography weight={400} size={14}>
                            {pg?.requestNumber}
                          </Typography>
                        </Stack>
                      </Grid>

                      {(selectedStatus === 'pending' || selectedStatus === 'on hold') && (
                        <Grid item xs={12} md={4} lg={2.5}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              REQUESTED BY
                            </Typography>
                            <Typography weight={400} size={14} color="#3E3E3E">
                              {pg.requestedBy}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      <Grid item xs={12} md={4} lg={3}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            REQUESTED DATE
                          </Typography>
                          <Typography weight={400} size={14} color="#3E3E3E">
                            {pg.requestDate}
                          </Typography>
                        </Stack>
                      </Grid>

                      {selectedStatus === 'approved' && (
                        <Grid item xs={12} md={4} lg={2.5}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              APPROVED DATE
                            </Typography>
                            <Typography weight={400} size={14} color="#3E3E3E">
                              {pg.modifiedDate}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}

                      {selectedStatus === 'rejected' && (
                        <Grid item xs={12} md={4} lg={2.5}>
                          <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                            <Typography weight={700} size={10} color="#3e3e3e">
                              REJECTED DATE
                            </Typography>
                            <Typography weight={400} size={14} color="#3E3E3E">
                              {pg.modifiedDate}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                      <Grid item xs={12} md={4} lg={2.5}>
                        <Stack alignItems="start" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
                          <Typography weight={700} size={10} color="#3e3e3e">
                            CUSTOMER NAME
                          </Typography>
                          <Typography weight={400} size={14} color="#3E3E3E">
                            {pg?.customerName}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid lg={1}>
                        <Stack direction="row" alignItems="center" justifyContent="end" gap={1} sx={{ height: '100%' }}>
                          <IconButton onClick={() => setRequestSelected(idx)}>
                            <Icon icon="akar-icons:chevron-right" fontSize={20} color="#456BD9" />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </>
            ) : (
              <Box p={0.5} mt={1}>
                <Center>
                  <Muitypo variant="body1">No requests found.</Muitypo>
                </Center>
              </Box>
            )}
          </Box>
          {!!paginated.length && (
            <Box>
              <Pagination
                count={optRequests.length}
                page={page}
                onItemsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                itemsPerPage={rowsPerPage}
                countOpts={[10, 20, 30, 50]}
              />
            </Box>
          )}
        </Grid>
        {requestSelected && (
          <Grid xs={7}>
            <Box
              p={(theme) => theme.spacing(2.5, 4.6, 3, 4.6)}
              m={(theme) => theme.spacing(1, 1, 0, 2.6)}
              borderRadius={1.5}
              bgcolor={(theme) => theme.palette.grey[50]}
              border={(theme) => `1px solid #E3E3E3`}
            >
              <Stack direction="row" alignItems="flex-end">
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <Muitypo variant="h4" fontWeight={500} fontSize={32} color="#1F1F1F">
                    {requestSelected?.requestNumber}
                  </Muitypo>
                  {(selectedStatus === 'pending' || selectedStatus === 'on hold') && (
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={handleCancelRequest}
                        disabled={processingRequest || !!requestSelected.decision}
                      >
                        {requestSelected.decision || 'Cancel Request'}
                      </Button>
                      {selectedStatus === 'on hold' && (
                        <Button
                          disabled={
                            assignableOrders.length === 0 ||
                            processingRequest ||
                            requestSelected?.decision === 'Requested'
                          }
                          sx={{ ml: 1, textTransform: 'none', fontWeight: 18 }}
                          variant="contained"
                          onClick={handleRequestAssignment}
                        >
                          Request Assignment
                        </Button>
                      )}
                    </Box>
                  )}
                </Stack>
              </Stack>
              {assignableOrders.length > 0 && selectedStatus === 'on hold' && (
                <Box my={1} sx={{ width: '50%' }}>
                  <Select
                    options={options}
                    setSelectedOption={(o) => {
                      const obj = assignableOrders.filter((ao) => ao.order.orderId === o);
                      console.log(o, 'selected order');
                      console.log(obj, 'obj order');
                      setSelectedAssignAbleOrders(obj[0]);
                      setSelectedOrder(obj[0]);
                      console.log(selectedAssignableOrders, 'selectedAss');
                    }}
                  />
                </Box>
              )}
              <Box my={1}>
                {requestSelected.comments && (
                  <Stack sx={{ bgcolor: '#f2f2f2', p: 1, borderRadius: 1 }}>
                    <Muitypo variant="caption" sx={{ fontSize: 10, color: (theme) => theme.palette.grey[600] }}>
                      Reason for request
                    </Muitypo>
                    <Muitypo variant="caption">{requestSelected.comments}</Muitypo>
                  </Stack>
                )}
              </Box>
              <VehicleCompare
                customerVehicle={
                  selectedAssignableOrders?.customerVehicle
                    ? selectedAssignableOrders?.customerVehicle
                    : requestSelected?.customerVehicle
                }
                inventoryVehicle={requestSelected?.inventoryVehicle}
                metaData={requestSelected}
                showDifferences={!!requestSelected?.customerVehicle}
                // transactionErrors={transactionErrors || requestSelected?.transactionErrors}
                // retryCall={retryAssignment}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Requests;
